import Commons from "oc-commons/Commons";
import Schrank from "./Schrank";
import Preisberechnung from "./partsAndPrices/Preisberechnung";
import Helper from "../model/Helper";
import Raum from "./Raum";
import { v2, v3 } from "oc-threejs/short3";
import Line2d from "oc-threejs/math/Line2d";
import ClosedInterval from "oc-commons/ClosedInterval";
import ArrangementO3 from "../object3d/Arrangment";
import Kunde from "./Kunde";
import Maths3 from "oc-threejs/Maths3";
import { Quaternion } from "three";
import { SchrankConnector, BOTTOM_TOP, LEFT_RIGHT } from "./SchrankConnector";

export default class Arrangement {
    // DO NOT CHANGE
    className = "Arrangement";

    // persistent (UserLayer) ------------------------------------------

    type;
    schraenke = [];
    currentSchrankIndex = 0;
    bemerkungen = null;
    kunde = new Kunde();

    selected = {
        schrankIdx: 0,
        elementIdx: -1,
        interieur: null,
    };

    defaultGriff = null;

    // transient (CompiledLayer) und cached -----------------------------

    object3d;

    // transient (CompiledLayer) und resetted vor Compilierung ----------

    raum;

    // Map I18N-Key auf Parameter-Array, meist leer
    hinweistexteMap = {};

    preisberechnung;
    arrangementPreis;
    partsList = {
        schraenke: [],
        model: {},
    };
    doorsClosed = true;

    maße = false;
    changeDoorSize = false;

    // eine Map Dekortyp -> Material.Name
    materials = {};
    sockelMaterials = {};

    fluegelanordungLinks = false;

    schrankConnectors = [];

    constructor() {
        this.preisberechnung = new Preisberechnung(this);
        this.raum = new Raum(this);
        this.materials = APP.createDekor2DefaultMaterial();
    }

    get selectedSchrank() {
        let schrank = this.schraenke[this.selected.schrankIdx];
        if (!schrank) {
            schrank = this.schraenke[this.schraenke.length - 1];
        }
        return schrank;
    }

    set selectedSchrank(schrank) {
        if (this.selected.schrankIdx !== this.schraenke.findIndex((s) => s === schrank)) {
            this.selected.schrankIdx = this.schraenke.findIndex((s) => s === schrank);
            this.selected.elementIdx = -1;
            APP.cameraAnimation();
        }
    }

    get selectedElement() {
        if (this.selected.elementIdx == -1) {
            return null;
        }
        return this.selectedSchrank?.elements[this.selected.elementIdx];
    }

    set selectedElement(element) {
        if (element) {
            this.selectedSchrank = element.schrank;
            this.selected.elementIdx = this.selectedSchrank.elements.findIndex((e) => e === element);
            this.selected.interieur = null;
        } else {
            this.selected.elementIdx = -1;
        }
    }

    setSockelHoeheForSelectedSchrank(hoehe) {
        this.selectedSchrank.sockelHoehe = hoehe;
        APP.model.setDirty();
    }

    setSockelOptionForSelectedSchrank(option) {
        this.selectedSchrank.sockelOption = option;
        APP.model.setDirty();
    }

    setDefaultGriff() {
        this.defaultGriff = this.schraenke[0].options.getValue('default_griff') || "Bogengriff";
    }

    setGriffArtikelnummer(nummer) {
        this.griffArtikelnummer = nummer;
        this.schraenke.forEach(schrank => schrank.griffArtikelnummer = nummer);
    }

    get currentGriffArtikelnummer() {
        return this.schraenke.length > 0 ? APP.arrangement.schraenke[0].griffArtikelnummer : null;
    }

    setExzenterverbindungOption(value) {
        this.schraenke.forEach((schrank) => {
            schrank.setExzenterverbindungOption(value);
        });
    }

    get selectedInterieur() {
        return this.selected.interieur;
    }

    set selectedInterieur(interieur) {
        if (interieur != null && interieur.isOrphaned()) {
            interieur = null;
        }
        this.selected.interieur = interieur;
        this.selected.elementIdx = -1;
    }

    ensureElementSelected() {
        if (this.selectedElement == null) {
            this.selectedElement = _.first(this.selectedSchrank.elements);
            APP.dirty();
        }
    }

    get hasInterieurDoors() {
        return this.schraenke.some((s) => s.hasInterieurDoors);
    }

    get hasFronts() {
        return this.schraenke.some((s) => s.hasFronts);
    }

    toJSON() {
        let result = {
            className: this.className,
            currentSchrank: this.currentSchrankIndex,
            schraenke: this.schraenke.map((c) => c.toJSON()),
            griffArtikelnummer: this.griffArtikelnummer,
            partsList: this.partsList,
            preis: this.preisberechnung.getPreis(),
            bemerkungen: this.bemerkungen,
            kunde: this.kunde.toJson(),
            stammdaten: this.kunde.toJson(),
            haendler: APP.haendler,
            materials: _.clone(this.materials),
            schrankConnectors: this.schrankConnectors.map((c) => c.toJSON()),
        };
        return result;
    }

    fromJSON(json) {
        this.schrankConnectors = Commons.mapreuse(
            json.schrankConnectors || [],
            this.schrankConnectors,
            (c) => new SchrankConnector(this),
            (d, s) => {
                return d.fromJSON(s);
            }
        );

        this.schraenke = Commons.mapreuse(
            json.schraenke || [],
            this.schraenke,
            (c) => new Schrank(this),
            (d, s) => {
                return d.fromJSON(s);
            }
        );

        if (json.kunde) {
            this.kunde.fromJson(json.kunde);
        }
        this.bemerkungen = json.bemerkungen;
        this.currentSchrankIndex = this.currentSchrankIndex;
        _.assign(this.materials, json.materials);
        this.heal(json);
        this.setDefaultGriff();
        // this.griffArtikelnummer = Commons.isEmpty(json.griffArtikelnummer) ? this.defaultGriff : json.griffArtikelnummer;
        this.setGriffArtikelnummer(Commons.isEmpty(json.griffArtikelnummer) ? this.defaultGriff : json.griffArtikelnummer)
        return this;
    }

    // Wenn keine Konfiguration muss der Konfigurator sich "selbst heilen" mit einem Default durch das init-Modell
    heal(json) {
        APP.healMaterials(this.materials, false);

        if (this.schraenke.length < 1) {
            const defaultSchrank = new Schrank();
            defaultSchrank.fromJSON({
                modell: json.Startmodell ? json.Startmodell : "Standard",
            });
            defaultSchrank.size.x = defaultSchrank.options.getFloat("schrank_breite");
            defaultSchrank.size.y = defaultSchrank.options.getFloat("schrank_hoehe");
            defaultSchrank.size.z = defaultSchrank.options.getFloat("schrank_tiefe");
            defaultSchrank.sizeTop.x = defaultSchrank.options.getFloat("kniestock_breiteoben");
            defaultSchrank.sizeTop.y = defaultSchrank.options.getFloat("kniestock");
            defaultSchrank.sizeTop.z = defaultSchrank.options.getFloat("kniestock_hinten");
            this.schraenke.push(defaultSchrank);
        }
    }

    convertEckschrankGesamtMasseToSchenkelMasse() {
        const [a, b] = this.schraenke.map((s) => s.size);
        if (a.z < b.z) {
            a.x -= b.z;
        } else {
            b.x -= a.z;
        }
    }

    copy(arrangement, createInterceptor) {
        this.bemerkungen = arrangement.bemerkungen;
        this.schraenke = Commons.mapreuse(
            arrangement.schraenke,
            this.schraenke || [],
            () => new Schrank(this),
            (d, s) => d.copy(s, createInterceptor)
        );
        // this.schiebetuerLinksVorne = arrangement.schiebetuerLinksVorne;
        // this.schliessdaempfung = arrangement.schliessdaempfung;
        _.assign(this.materials, arrangement.materials);
        return this;
    }

    compile() {
        APP.model.lazyCreateObject3d(this, () => {
            const o = new ArrangementO3(this);
            if (this.object3d) {
                Scenes.remove(this.object3d);
            }
            this.object3d = o;
            APP.model.add(o);
            return o;
        });
        //Raum
        this.raum.compile();
        //Schrankverbindungen
        this.schrankConnectors.forEach((connector) => connector.compile());
        // Das ist nicht perfekt.. Aber erstmal benötigt, damit Fehler in Ecksystemen berücksichtigt werden können
        // Grundsätzlich muss der Algo für die Verdeckung erstmal neu...
        // Dann würde 1x compile auch reichen.. Die Verdeckung kompiliert sich anhand des O3s.. Murgs
        this.schraenke.forEach((schrank) => schrank.compile());
        this.schraenke.forEach((schrank) => schrank.compile());
        this._compileLocations();

        //TODO: Was ist das?
        // APP.updatePrices(this.toJSON());
        this.preis = this.preisberechnung.getPreis();
        // this.compilePreisLazy();

        this.compiled = true;
    }

    _compileAussen() {
        this.schraenke.forEach((x) => x.compileBox());
        this._compileLocations();
        this._compileVerdeckung();
    }

    compilePreisLazy = _.debounce(() => {
        this.preis = this.preisberechnung.getPreis();
    }, 300);

    _compileLocations() {
        const raumBox = this.raum.box.clone();

        // Positioniert wird nur in der Ausgangsstellung wenn ein Schrank da ist.
        if (this.schraenke.length === 1) {
            const schrank = this.schraenke[0];
            schrank.quaternion.copy(Maths3.quaternion.zero);
            Scenes.bring({
                what: [schrank, schrank.box.grid(0.5, 0, 0)],
                to: [this.raum, raumBox.grid(0.5, 0, 0)],
                translate: true,
            });
        }

       /*  raumBox.expandByVector(v3(-2, -Math.max(0.1, APP.settings.getFloat("schrank_abstand_unten")), -2));
        if (this.schraenke.length == 1) {
            const schrank = this.schraenke[0];
            schrank.quaternion.copy(Maths3.quaternion.zero);
            Scenes.bring({
                what: [schrank, schrank.box.grid(0.5, 0, 0)],
                to: [this.raum, raumBox.grid(0.5, 0, 0)],
                translate: true,
            });
        } */
    }

    compileVerdeckungForSchraenke(schrank1, schrank2) {
        const verdeckungMinDistance = 9;
        const schraenke = [schrank1, schrank2];

        // Betrachte die Schränke stets von oben, 2d als Rechtecke in der Ebene
        // Eine Verdeckung ergibt sich, wenn der Abstand zweier Seiten (Linien-Segment) zweier Schränke
        // einen Mindestwert unterschreitet
        // gehe alle Kombinationsmöglichkeiten der Schränke durch
        const ecke = (schrank, x, z) => {
            const vec3 = schrank.localToWorld(schrank.box.grid(v3(x, 0, z)));
            return v2(vec3.x, vec3.z);
        };

        const kanten = (schrank) => {
            const ecken = [ecke(schrank, 0, 0), ecke(schrank, 0, 1), ecke(schrank, 1, 1), ecke(schrank, 1, 0)];
            const element = [_.first(schrank.elements), null, _.last(schrank.elements), null];
            const edges = [];
            for (let i = 0; i < ecken.length; i++) {
                const j = (i + 1) % ecken.length;
                const e1 = ecken[i];
                const e2 = ecken[j];
                const line = new Line2d().setFromPoints(e1, e2);
                line.element = element[i];
                edges.push(line);
            }
            return edges;
        };

        const lineProjectionInterval = (line, p1, p2) =>
            ClosedInterval.order([line.origin.distanceTo(line.projectPoint(p1)), line.origin.distanceTo(line.projectPoint(p2))]);

        const verdeckung = (a, oeffnung, b, kante) => {
            // console.log('-----------------------------');
            // console.log('oeffnung',oeffnung,'kante',kante);
            // console.log('intersection',oeffnung.intersectLine(kante));
            const oeffnungInterval = lineProjectionInterval(oeffnung, oeffnung.origin, oeffnung.point2);
            // console.log('oeffnungInterval',oeffnungInterval);
            const kanteInterval = lineProjectionInterval(oeffnung, kante.origin, kante.point2);
            //console.log('kanteInterval',kanteInterval);
            const points = oeffnung.pointsWhereDistanceToLineIs(kante, verdeckungMinDistance);
            //console.log('points',points);
            let verdeckungsInterval = kanteInterval;
            if (points == null) {
                const parallelDistance = oeffnung.distanceToPoint(kante.origin);
                //console.log('parallelDistance',parallelDistance);
                if (parallelDistance > verdeckungMinDistance) {
                    return;
                }
            } else {
                const pointsInterval = lineProjectionInterval(oeffnung, points[0], points[1]);
                //console.log('pointsInterval',pointsInterval);
                verdeckungsInterval = ClosedInterval.intersection(kanteInterval, pointsInterval);
                //console.log('verdeckungsInterval',verdeckungsInterval);
            }
            verdeckungsInterval = verdeckungsInterval ? ClosedInterval.intersection(oeffnungInterval, verdeckungsInterval) : null;
            //console.log('verdeckungsInterval',verdeckungsInterval);
            if (verdeckungsInterval) {
                a.verdeckungsIntervale.push(verdeckungsInterval);
                a.verdeckungsIntervale = ClosedInterval.removePoints(ClosedInterval.maybeUnion(a.verdeckungsIntervale));
                if (kante.element) {
                    const elemente = a.elements.filter((e) => ClosedInterval.intersects([e.box.min.x, e.box.max.x], verdeckungsInterval));
                    kante.element.verdecktAnderenSchrank = { schrank: a, verdeckungsInterval, elemente };
                    elemente.forEach((e) => e.verdecktVonElementen.push(kante.element));
                }
            }
            //console.log('a.verdeckungsIntervale',JSON.stringify(a.verdeckungsIntervale));
        };

        const setSchrankVerdeckungen = (a, b) => {
            // Bei der Verdecken wird geprüft, ob die Öffnungsseite des Schrankes a von links oder rechts aus von
            // einem anderen Schrank b verdeckt ist.
            const oeffnung = new Line2d().setFromPoints(ecke(a, 0, 1), ecke(a, 1, 1));
            kanten(b).forEach((kante) => verdeckung(a, oeffnung, b, kante));
        };

        const setNoVerdeckung = (schraenke) => {
            schraenke.forEach((a) => {
                a.verdeckungsIntervale = [];
                a.elements.forEach((e) => {
                    e.verdecktAnderenSchrank = null;
                    e.unverdeckt.linksVerdeckt = false;
                    e.unverdeckt.rechtsVerdeckt = false;
                    e.unverdeckt.box.copy(e.box);
                    e.unverdeckt.size.copy(e.size);
                    e.verdecktVonElementen = [];
                });
            });
        };

        const setElementVerdeckung = (element) => {
            const elementInterval = [element.box.min.x, element.box.max.x];
            const fuge = element.schrank.options.getFloat("fugen_abstand");
            const verdeckungsInterval = element.schrank.verdeckungsIntervale
                .map((i) => ClosedInterval.intersection(elementInterval, i))
                .filter((i) => i !== null)
                .map((i) => ClosedInterval.intersection(elementInterval, ClosedInterval.expandBy(i, fuge)))
                .reduce((acc, i) => ClosedInterval.union(acc, i), null);
            if (verdeckungsInterval && ClosedInterval.size(verdeckungsInterval) > 0.01) {
                const l = verdeckungsInterval[0] - elementInterval[0];
                const r = elementInterval[1] - verdeckungsInterval[1];
                const unverdecktesInterval = l < r ? [verdeckungsInterval[1], elementInterval[1]] : [elementInterval[0], verdeckungsInterval[0]];
                const unverdeckt = element.unverdeckt;
                unverdeckt.box.min.x = unverdecktesInterval[0];
                unverdeckt.box.max.x = unverdecktesInterval[1];
                unverdeckt.size.x = unverdeckt.box.max.x - unverdeckt.box.min.x;
                unverdeckt.linksVerdeckt = Math.abs(element.box.min.x - unverdeckt.box.min.x) > 0.01;
                unverdeckt.rechtsVerdeckt = Math.abs(element.box.max.x - unverdeckt.box.max.x) > 0.01;
            }
        };

        setNoVerdeckung(schraenke);

        setSchrankVerdeckungen(schrank1, schrank2);
        setSchrankVerdeckungen(schrank2, schrank1);

        schraenke.forEach((a) => {
            a.elements.forEach((e) => setElementVerdeckung(e));
        });
    }

    _compileVerdeckung() {
        const verdeckungMinDistance = 9;

        // Betrachte die Schränke stets von oben, 2d als Rechtecke in der Ebene
        // Eine Verdeckung ergibt sich, wenn der Abstand zweier Seiten (Linien-Segment) zweier Schränke
        // einen Mindestwert unterschreitet
        // gehe alle Kombinationsmöglichkeiten der Schränke durch
        const ecke = (schrank, x, z) => {
            const vec3 = schrank.localToWorld(schrank.box.grid(v3(x, 0, z)));
            return v2(vec3.x, vec3.z);
        };

        const kanten = (schrank) => {
            const ecken = [ecke(schrank, 0, 0), ecke(schrank, 0, 1), ecke(schrank, 1, 1), ecke(schrank, 1, 0)];
            const element = [_.first(schrank.elements), null, _.last(schrank.elements), null];
            const edges = [];
            for (let i = 0; i < ecken.length; i++) {
                const j = (i + 1) % ecken.length;
                const e1 = ecken[i];
                const e2 = ecken[j];
                const line = new Line2d().setFromPoints(e1, e2);
                line.element = element[i];
                edges.push(line);
            }
            return edges;
        };

        const schrankKombinationen = (iterator) => {
            for (let i = 0; i < this.schraenke.length; i++) {
                for (let j = i + 1; j < this.schraenke.length; j++) {
                    const a = this.schraenke[i];
                    const b = this.schraenke[j];
                    if (a.isEckSchrankWith(b)) {
                        iterator(a, b);
                    }
                }
            }
        };

        const lineProjectionInterval = (line, p1, p2) =>
            ClosedInterval.order([line.origin.distanceTo(line.projectPoint(p1)), line.origin.distanceTo(line.projectPoint(p2))]);

        const verdeckung = (a, oeffnung, b, kante) => {
            // console.log('-----------------------------');
            // console.log('oeffnung',oeffnung,'kante',kante);
            // console.log('intersection',oeffnung.intersectLine(kante));
            const oeffnungInterval = lineProjectionInterval(oeffnung, oeffnung.origin, oeffnung.point2);
            // console.log('oeffnungInterval',oeffnungInterval);
            const kanteInterval = lineProjectionInterval(oeffnung, kante.origin, kante.point2);
            //console.log('kanteInterval',kanteInterval);
            const points = oeffnung.pointsWhereDistanceToLineIs(kante, verdeckungMinDistance);
            //console.log('points',points);
            let verdeckungsInterval = kanteInterval;
            if (points == null) {
                const parallelDistance = oeffnung.distanceToPoint(kante.origin);
                //console.log('parallelDistance',parallelDistance);
                if (parallelDistance > verdeckungMinDistance) {
                    return;
                }
            } else {
                const pointsInterval = lineProjectionInterval(oeffnung, points[0], points[1]);
                //console.log('pointsInterval',pointsInterval);
                verdeckungsInterval = ClosedInterval.intersection(kanteInterval, pointsInterval);
                //console.log('verdeckungsInterval',verdeckungsInterval);
            }
            verdeckungsInterval = verdeckungsInterval ? ClosedInterval.intersection(oeffnungInterval, verdeckungsInterval) : null;
            //console.log('verdeckungsInterval',verdeckungsInterval);
            if (verdeckungsInterval) {
                a.verdeckungsIntervale.push(verdeckungsInterval);
                a.verdeckungsIntervale = ClosedInterval.removePoints(ClosedInterval.maybeUnion(a.verdeckungsIntervale));
                if (kante.element) {
                    const elemente = a.elements.filter((e) => ClosedInterval.intersects([e.box.min.x, e.box.max.x], verdeckungsInterval));
                    kante.element.verdecktAnderenSchrank = { schrank: a, verdeckungsInterval, elemente };
                    elemente.forEach((e) => e.verdecktVonElementen.push(kante.element));
                }
            }
            //console.log('a.verdeckungsIntervale',JSON.stringify(a.verdeckungsIntervale));
        };

        const setSchrankVerdeckungen = (a, b) => {
            // Bei der Verdecken wird geprüft, ob die Öffnungsseite des Schrankes a von links oder rechts aus von
            // einem anderen Schrank b verdeckt ist.
            const oeffnung = new Line2d().setFromPoints(ecke(a, 0, 1), ecke(a, 1, 1));
            kanten(b).forEach((kante) => verdeckung(a, oeffnung, b, kante));
        };

        const setNoVerdeckung = () => {
            this.schraenke.forEach((a) => {
                a.verdeckungsIntervale = [];
                a.elements.forEach((e) => {
                    e.verdecktAnderenSchrank = null;
                    e.unverdeckt.linksVerdeckt = false;
                    e.unverdeckt.rechtsVerdeckt = false;
                    e.unverdeckt.box.copy(e.box);
                    e.unverdeckt.size.copy(e.size);
                    e.verdecktVonElementen = [];
                });
            });
        };

        const setElementVerdeckung = (element) => {
            const elementInterval = [element.box.min.x, element.box.max.x];
            const fuge = element.schrank.options.getFloat("fugen_abstand");
            const verdeckungsInterval = element.schrank.verdeckungsIntervale
                .map((i) => ClosedInterval.intersection(elementInterval, i))
                .filter((i) => i !== null)
                .map((i) => ClosedInterval.intersection(elementInterval, ClosedInterval.expandBy(i, fuge)))
                .reduce((acc, i) => ClosedInterval.union(acc, i), null);
            if (verdeckungsInterval && ClosedInterval.size(verdeckungsInterval) > 0.01) {
                const l = verdeckungsInterval[0] - elementInterval[0];
                const r = elementInterval[1] - verdeckungsInterval[1];
                const unverdecktesInterval = l < r ? [verdeckungsInterval[1], elementInterval[1]] : [elementInterval[0], verdeckungsInterval[0]];
                const unverdeckt = element.unverdeckt;
                unverdeckt.box.min.x = unverdecktesInterval[0];
                unverdeckt.box.max.x = unverdecktesInterval[1];
                unverdeckt.size.x = unverdeckt.box.max.x - unverdeckt.box.min.x;
                unverdeckt.linksVerdeckt = Math.abs(element.box.min.x - unverdeckt.box.min.x) > 0.01;
                unverdeckt.rechtsVerdeckt = Math.abs(element.box.max.x - unverdeckt.box.max.x) > 0.01;
            }
        };

        setNoVerdeckung();
        schrankKombinationen((a, b) => {
            setSchrankVerdeckungen(a, b);
            setSchrankVerdeckungen(b, a);
        });
        this.schraenke.forEach((a) => {
            a.elements.forEach((e) => setElementVerdeckung(e));
        });
    }

    getHinweistexteFormatted() {
        if (Commons.isEmpty(this.hinweistexteMap)) {
            return null;
        }
        return {
            title: Commons.isEmpty(this.hinweistexteMap) ? "" : I18N(this.hinweistexteMap.title),
            messages: _.keys(this.hinweistexteMap)
                .filter((key) => key !== "title")
                .map((key) => I18N(...[key].concat(this.hinweistexteMap[key]))),
        };
    }

    setHinweisText(i18nKey, ...params) {
        this.hinweistexteMap[i18nKey] = params;
    }

    setHinweisTextAndTitle(title, i18nKey, ...params) {
        this.hinweistexteMap.title = title;
        const userParams = params.map((p) => (isNaN(p) ? p : Helper.round(p)));
        this.setHinweisText(i18nKey, ...userParams);
    }

    resetHinweistexte() {
        this.hinweistexteMap = {};
    }

    clearLocked(direction = null) {
        this.schraenke.forEach((c) => c.clearLocked(direction));
    }

    getElements() {
        return this.schraenke.reduce((acc, schrank) => acc.concat(schrank.elements), []);
    }

    getInterieurs() {
        return _.flattenDeep(this.schraenke.map((schrank) => schrank.elements.map((element) => element.interieurs)));
    }

    async addSchrank(info) {
        try {
            const { schrank, selected, direction, offset, rotation, newSchrankInEcke } = info;
            const inspiration_code = selected.addable;
            const inspiration = await (await fetch(`${APP.settings.siteUrl}?Code=${inspiration_code}&api=1&fmt=configuration`)).json();
            APP.model.tryCompile(() => {
                // Es wird immer nur EIN Schrank hinzugefügt. Mehr geht erstmal nicht.
                if (inspiration) {
                    const newSchrankJson = inspiration.Json.schraenke[inspiration.Json.currentSchrank];
                    const newSchrank = new Schrank(this);
                    // Den neuen Schrank erstmal so hinstellen, wie der alte.. Danach compiled mit "rotation"
                    newSchrank.quaternion = new THREE.Quaternion().copy(schrank.quaternion);
                    newSchrank.fromJSON({
                        ...newSchrankJson,
                        createdFromInspiration: inspiration_code,
                    });

                    newSchrank.compile();
                    this.schraenke.push(newSchrank);
                    schrank.neighbors[direction] = newSchrank;

                    if (direction === "left") {
                        newSchrank.neighbors["right"] = schrank;
                    }
                    if (direction === "right") {
                        newSchrank.neighbors["left"] = schrank;
                    }

                    const schrankIndex = this.schraenke.indexOf(schrank);
                    const newSchrankIndex = this.schraenke.indexOf(newSchrank);

                    const schrankConnector = new SchrankConnector();
                    schrankConnector.fromJSON({
                        indices: [this.schraenke.indexOf(schrank), this.schraenke.indexOf(newSchrank)],
                        rotation: rotation,
                        offset: offset,
                        backIndex: rotation ? (newSchrankInEcke ? newSchrankIndex : schrankIndex) : null,
                        type: direction === "top" ? BOTTOM_TOP : LEFT_RIGHT,
                        layout:
                            direction === "top"
                                ? {
                                      bottom: schrankIndex,
                                      top: newSchrankIndex,
                                  }
                                : direction === "right"
                                ? {
                                      left: schrankIndex,
                                      right: newSchrankIndex,
                                  }
                                : direction === "left"
                                ? {
                                      left: newSchrankIndex,
                                      right: schrankIndex,
                                  }
                                : {},
                    });
                    this.schrankConnectors.push(schrankConnector);
                    schrankConnector.compile();

                    schrank.setDimension("x", schrank.size.x, true);
                    schrank.setDimension("y", schrank.size.y, true);
                    schrank.setDimension("z", schrank.size.z, true);

                    APP.onOnce("changedConfiguration", () => {
                        this.selectedSchrank = newSchrank;
                    });
                }
            });
        } catch(e) {
            APP.arrangement.setHinweisTextAndTitle("CouldNotFetchInspiration", "Konnte Inspiration nicht laden. Internetverbindung prüfen.");
            model.maybeShowHinweistexteDialog();
        }
    }

    deleteSchrank(schrank) {
        if (schrank && schrank.connectors.length === 1) {
            schrank.delete();
            this.schrankConnectors = this.schrankConnectors.filter((schrankConnector) => !schrankConnector.schraenke.includes(schrank));
            this.schrankConnectors.forEach((schrankConnector) => schrankConnector.lock());
            this.schraenke = this.schraenke.filter((aSchrank) => aSchrank !== schrank);
            this.schrankConnectors.forEach((schrankConnector) => schrankConnector.renew());
            if(this.currentSchrankIndex >= this.schraenke.length) {
                this.currentSchrankIndex = this.schraenke.length-1;
            }
        }
    }

    getBox3World() {
        const unionBox = new THREE.Box3();
        this.schraenke.forEach((s) => s.getBox3World(unionBox));
        return unionBox;
    }

    iterate(iterator) {
        iterator(this);
        this.schraenke.forEach((x) => x.iterate(iterator));
        iterator(this.kunde);
        iterator(this.raum);
    }

    iterateElements(iterator) {
        this.schraenke.forEach((s) => s.iterateElements(iterator));
    }

    iterateInterieur(iterator) {
        this.iterateElements((e) => e.interieurs.forEach((x) => x.iterate(iterator)));
    }

    /**
     * ist false, falls dieses Objekt Teil des APP.arrangement ist.
     */
    isOrphaned() {
        return this !== APP.arrangement;
    }

    getChangedProductsHinweistexte() {
        // TODO, Rückgabe von Texten, falls Produkte nicht mehr verfügbar sind
        // das hier war aus dem DataProvider und machte auf bescheurte Weise die Ermittlung, welche Produkte nicht mehr
        // vorhanden sind
        // const schrankA = arrangement.schraenke[0];
        // let schrankC = configuration.schraenke[0];
        // let griff = _.get(schrankC, 'doorHandle', null);
        // if (griff !== null && griff !== schrankA['doorHandleArtikelnummer']) {
        //     result.push('missingGriffeText')
        // }
        // if (schrankA.materials.corpus !== schrankC.materials.corpus || schrankA.materials.front !== schrankC.materials.front) {
        //     result.push('missingTextureFrontText')
        // }
        return [];
    }

    changeVisibilityForSchraenkeImWeg(visible = true) {
        this.schraenke.forEach((schraenke) => {
            schraenke.object3d.visible = visible;
        });
    }
}
